import useDateFormatters from "@hooks/useDateFormatters"

import {
    GenerationModeBadge,
    JobCompletionBadge,
    JobCountBadge,
    RecurrenceBadge,
    SeriesDateBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { composeClientLocation, shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { JobSeriesCardProps } from "./JobSeriesCard.types"

export default function JobSeriesCard(props: JobSeriesCardProps) {
    const { series, displayedAttributes, onClick, isLoading = false, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    // A series is closed once its recurrence has ended; its last modification stands in for the close time.
    const isClosed = series.status_label === "Closed"
    const statusTooltip = formatDatetimeTooltip(
        isClosed ? "Closed" : "Created",
        isClosed ? series.last_modified_at : series.created_at,
    )

    const showGenerationMode = shouldShowAttribute(displayedAttributes, "type_label")
    const showRecurrence = shouldShowAttribute(displayedAttributes, "recurrence_interval")
    const showStartDate =
        shouldShowAttribute(displayedAttributes, "recurrence_start") && Boolean(series.recurrence_start)
    const showEndDate = shouldShowAttribute(displayedAttributes, "recurrence_end")
    const showCompletion =
        shouldShowAttribute(displayedAttributes, "completion_progress") && series.total_jobs_count > 0
    const showOpenJobs = shouldShowAttribute(displayedAttributes, "open_jobs_count")

    const hasAttributes =
        showGenerationMode || showRecurrence || showStartDate || showEndDate || showCompletion || showOpenJobs

    return (
        <ObjectCard onClick={onClick} isLoading={isLoading} className={className} {...errorProps}>
            <ObjectCard.Header
                id={series.id}
                statusBadge={
                    <ObjectStatusBadge
                        objectType="job_series"
                        status={series.status_label}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body primaryText={series.service_name} secondaryText={composeClientLocation(series)} />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showGenerationMode && <GenerationModeBadge mode={series.type_label} />}
                    {showRecurrence && (
                        <RecurrenceBadge
                            interval={series.recurrence_interval}
                            frequency={series.recurrence_frequency}
                            startDate={series.recurrence_start}
                            weeklyRecurrence={series.weekly_recurrence}
                            monthlyRecurrence={series.monthly_recurrence}
                            endDate={series.recurrence_end}
                        />
                    )}
                    {showStartDate && <SeriesDateBadge variant="start" date={series.recurrence_start} />}
                    {showEndDate && <SeriesDateBadge variant="end" date={series.recurrence_end} />}
                    {showCompletion && (
                        <JobCompletionBadge completed={series.completed_jobs_count} total={series.total_jobs_count} />
                    )}
                    {showOpenJobs && (
                        <JobCountBadge
                            count={series.open_jobs_count}
                            noun="open"
                            pluralizeNoun={false}
                            tooltip={`Open jobs\n${series.open_jobs_count}`}
                        />
                    )}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
